/**
 * VideoBlock — Inline video embed (YouTube, Twitch clip, or uploaded)
 */
import { useRef } from "react";
import { SurfaceVideoConfigResponse } from "@/hooks/hub/useGetHub";
import { useElectronAudioForVideo } from "@/utils/electronAudio";
import "./VideoBlock.scss";

interface VideoBlockProps {
    config?: SurfaceVideoConfigResponse;
    sectionTitle?: string;
}

function getYouTubeEmbedUrl(url: string): string | null {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

function getTwitchClipEmbedUrl(url: string): string | null {
    const match = url.match(/clips\.twitch\.tv\/([a-zA-Z0-9_-]+)/);
    if (match) {
        const parent = typeof window !== "undefined" ? window.location.hostname : "localhost";
        return `https://clips.twitch.tv/embed?clip=${match[1]}&parent=${parent}`;
    }
    return null;
}

export const VideoBlock = ({ config, sectionTitle }: VideoBlockProps) => {
    /*
     * Electron: an uploaded video exposes only native controls (no React mute
     * state), so lift the shell's renderer mute off the element's real audible
     * state — sound plays in the app once the user unmutes (see
     * @/utils/electronAudio). No-ops on plain web. Hook stays above the early
     * return to satisfy rules-of-hooks.
     */
    const videoRef = useRef<HTMLVideoElement>(null);
    useElectronAudioForVideo(videoRef);

    if (!config) return null;

    const aspectRatio = config.aspectRatio || "16:9";
    const [w, h] = aspectRatio.split(":").map(Number);
    const paddingTop = w && h ? `${h / w * 100}%` : "56.25%"; // fallback to 16:9

    const maxHeightStyle = config.maxHeight ? { maxHeight: `${config.maxHeight}px` } : undefined;

    let embedContent: JSX.Element | null = null;

    if (config.source === "YOUTUBE" && config.videoUrl) {
        const embedUrl = getYouTubeEmbedUrl(config.videoUrl);
        if (embedUrl) {
            embedContent =
                <iframe
                    src={embedUrl}
                    className="video-block__iframe"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Video"
                />;
        }
    } else if (config.source === "TWITCH_CLIP" && config.videoUrl) {
        const embedUrl = getTwitchClipEmbedUrl(config.videoUrl);
        if (embedUrl) {
            embedContent =
                <iframe
                    src={embedUrl}
                    className="video-block__iframe"
                    allowFullScreen
                    title="Twitch Clip"
                />;
        }
    } else if (config.source === "UPLOAD" && config.videoUrl) {
        embedContent =
            <video
                ref={videoRef}
                className="video-block__video"
                src={config.videoUrl}
                poster={config.posterImageUrl}
                autoPlay={config.autoplay}
                loop={config.loop}
                muted={config.autoplay}
                controls
                playsInline
            />;
    }

    if (!embedContent) return null;

    return (
        <div className="video-block" style={maxHeightStyle}>
            {sectionTitle && <h3 className="video-block__title">{sectionTitle}</h3>}
            <div className="video-block__wrapper" style={{ paddingTop }}>
                {embedContent}
            </div>
        </div>
    );
};
